import { Routes, Route, Navigate } from 'react-router-dom';
import { lazy } from 'react';
import { useAuth } from '../context/AuthContext';

// Layouts
import PublicLayout from '../components/layout/PublicLayout';
import DashboardLayout from '../components/dashboard/DashboardLayout';

// Public Pages
import Home from '../pages/home/page';
import Login from '../pages/login/page';
import About from '../pages/about/page';
import Careers from '../pages/careers/page';
import Privacy from '../pages/privacy/page';
import Terms from '../pages/terms/page';

// Institution Auth (temporarily disabled)
// import InstitutionLayout from '../pages/auth/institution/InstitutionLayout';
// import SelectInstitution from '../pages/auth/institution/SelectInstitution';
// import RegisterInstitution from '../pages/auth/institution/RegisterInstitution';
// import VerifyOTP from '../pages/auth/institution/VerifyOTP';
// import InstitutionLogin from '../pages/auth/institution/InstitutionLogin';

// Dashboard Pages
import Overview from '../pages/dashboard/Overview';
import Resumes from '../pages/dashboard/Resumes';
import MessageComposer from '../pages/dashboard/MessageComposer';
import Referrals from '../pages/dashboard/Referrals';
import Credits from '../pages/dashboard/Credits';
import Analytics from '../pages/dashboard/Analytics';

// Profile Components
import ProfileLayout from '../components/profile/ProfileLayout';
import ProfileOverview from '../pages/dashboard/ProfileOverview';
import PersonalInfo from '../pages/profile/PersonalInfo';
import ResumeEditorPage from '../pages/editor/ResumeEditorPage';
import CoverLetterEditorPage from '../pages/cover-letter/CoverLetterEditorPage';

// Feature Pages (Protected)
import InterviewSetup from '../pages/interview-setup/page';
import SimulationSetup from '../pages/simulation-setup/page';
const VideoInterview = lazy(() => import('../pages/video-interview/page'));
import Simulation from '../pages/simulation/page'; // AI Simulation Room
import SimulationResult from '../pages/simulation-result/page'; // AI Simulation Results
import AIInterview from '../pages/ai-interview/page';
import ProfileSetup from '../pages/profile-setup/page';
import ProtectedRoute from './ProtectedRoute';
import InterviewDetail from '../pages/interview-detail/page';
import InterviewMode from '../pages/interview-mode/page';
import Settings from '../pages/settings/page';
import type { JSX } from 'react';

// Helper for Redirecting Authenticated Users
// If user is logged in and tries to access public home/login, redirect to dashboard
const PublicOnlyRoute = ({ children }: { children: JSX.Element }) => {
    const { isAuthenticated } = useAuth();
    if (isAuthenticated) {
        return <Navigate to="/dashboard/overview" replace />;
    }
    return children;
};

export const AppRoutes = () => {
    return (
        <Routes>
            {/* Public Routes - Wrapped in PublicLayout */}
            {/* We use PublicOnlyRoute to redirect logged-in users to Dashboard. 
          This strictly enforces "HIDE public tabs... SHOW dashboard tabs" by moving them to the Dashboard environment. */}
            <Route element={<PublicLayout />}>
                <Route path="/" element={
                    <PublicOnlyRoute>
                        <Home />
                    </PublicOnlyRoute>
                } />
                <Route path="/about" element={<About />} />
                <Route path="/careers" element={<Careers />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/terms" element={<Terms />} />
            </Route>

            {/* Login is usually standalone or public */}
            <Route path="/login" element={
                <PublicOnlyRoute>
                    <Login />
                </PublicOnlyRoute>
            } />

            {/* Institution Auth Routes (temporarily disabled) */}
            {/* <Route path="/auth/institution" element={
                <PublicOnlyRoute>
                    <InstitutionLayout />
                </PublicOnlyRoute>
            }>
                <Route index element={<SelectInstitution />} />
                <Route path="register" element={<RegisterInstitution />} />
                <Route path="verify" element={<VerifyOTP />} />
                <Route path="login" element={<InstitutionLogin />} />
            </Route> */}

            {/* V2 Dashboard Routing - Protected */}
            <Route path="/dashboard" element={<DashboardLayout />}>
                <Route index element={<Navigate to="overview" replace />} />
                <Route path="overview" element={<Overview />} />
                <Route path="resumes" element={<Resumes />} />
                <Route path="messages" element={<MessageComposer />} />
                <Route path="referrals" element={<Referrals />} />
                <Route path="credits" element={<Credits />} />
                <Route path="analytics" element={<Analytics />} />
                <Route path="profile" element={<ProfileLayout />}>
                    <Route index element={<ProfileOverview />} />
                    <Route path="personal-info" element={<PersonalInfo />} />
                </Route>
            </Route>

            {/* Protected Standalone Feature Routes */}
            <Route path="/profile-setup" element={<ProtectedRoute><ProfileSetup /></ProtectedRoute>} />
            <Route path="/interview-setup" element={<ProtectedRoute><InterviewSetup /></ProtectedRoute>} />
            <Route path="/simulation-setup" element={<ProtectedRoute><SimulationSetup /></ProtectedRoute>} />
            <Route path="/video-interview" element={<ProtectedRoute><VideoInterview /></ProtectedRoute>} />
            <Route path="/video-interview/:id" element={<ProtectedRoute><VideoInterview /></ProtectedRoute>} />
            <Route path="/simulation/:id" element={<ProtectedRoute><Simulation /></ProtectedRoute>} />
            <Route path="/simulation-result/:id" element={<ProtectedRoute><SimulationResult /></ProtectedRoute>} />
            <Route path="/ai-interview" element={<ProtectedRoute><AIInterview /></ProtectedRoute>} />
            <Route path="/interview-mode" element={<ProtectedRoute><InterviewMode /></ProtectedRoute>} />
            <Route path="/interview-detail/:id" element={<ProtectedRoute><InterviewDetail /></ProtectedRoute>} />
            <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />

            {/* Resume Editor */}
            <Route path="/app/resumes/:resumeId/editor" element={<ProtectedRoute><ResumeEditorPage /></ProtectedRoute>} />
            <Route path="/app/cover-letters/:id/editor" element={<ProtectedRoute><CoverLetterEditorPage /></ProtectedRoute>} />

            {/* Fallback */}
            <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
    );
};
